import { BundledSettingsProvider } from '../../../../configuration/bundledSettingsProvider';
import type { RoseCardAPIResponseContract } from '../RoseCardContract';
import { RoseAPIClient } from './RoseAPIClient';
import { SliderHTMLElement } from '../../../../common/slider/slider';
import { render } from '../../../../runtime/nanoTemplate';
import type { ReplacementTemplate } from '../../../replacementTemplate/replacementTemplate';

interface RoseCard {
    id: string;
    url: string;
    cover: string;
    title: string;
    typology: string;
    session: string;
}

const DATA_PRODUCT_CODES = 'data-product-codes';

const ATTRIBUTES = [DATA_PRODUCT_CODES, 'data-edit-mode'];

export class RoseBannerHTMLElement extends SliderHTMLElement {

    private apiClient: RoseAPIClient;

    private readonly disabled: boolean;

    constructor() {
        super({
            containerSelector: '.carousel-items-container',
            itemsContainerSelector: '.carousel-items',
            itemSelector: '.carousel-item',
            nextNavSelector: '.carousel-control-next',
            prevNavSelector: '.carousel-control-prev',
            sliderViewportSelector: '.banner-card-list'
        });
        this.sliderType = 'RoseBanner';

        this.disabled = BundledSettingsProvider.newInstance().getSettingSync('rose/disableBanners') as boolean || false;
    }

    static get observedAttributes(): string[] {
        return ATTRIBUTES;
    }

    public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (!ATTRIBUTES.includes(name)) {
            return;
        }

        if (oldValue === newValue) {
            return;
        }

        super.attributeChangedCallback(name, oldValue, newValue);

        setTimeout(() => {
            if (name === DATA_PRODUCT_CODES) {
                this.applyBindings(newValue);
            }
        });
    }

    private async applyBindings(productCodes: string): Promise<void> {
        let cards: RoseCard[] = [];
        if (this.disabled) {
            this.setAttribute('data-rose-banner-disabled', 'true');
        } else {
            const roseProducts: RoseCardAPIResponseContract = await this.getCards(productCodes);
            cards = (roseProducts?.data || []).map(contract => {
                const customFields = contract.customFields;
                const languages = customFields?.reply_col_session_language.map((lang) => lang.split(/[,_\s]/)[2]).join('/');
                const sessionNumber = customFields?.reply_col_session_meetings;
                const sessionDurationHour = customFields?.reply_col_session_duration;
                let session: string = languages;
                if (sessionNumber > 0) {
                    session += ` • ${sessionNumber} ${sessionNumber === 1 ? 'session' : 'sessions'}`;
                }
                if (sessionDurationHour) {
                    session += ` x ${sessionDurationHour}h`;
                }
                return {
                    id: contract.productNumber,
                    url: contract.productUrl,
                    cover: contract.cover?.media?.url,
                    title: contract.name,
                    typology: contract.tags[0]?.name,
                    session: session
                };
            });
            this.itemsTotal = cards.length;
        }

        const template = this.querySelector('#rose-card-template') as HTMLTemplateElement | ReplacementTemplate;
        const container = this.querySelector('.rose-cards-slider__items') as HTMLElement;

        if (template && container) {
            // Remove existing dynamic cards (keep only the last static card)
            const existingCards = container.querySelectorAll('.rose-cards-slider__item');
            for (let index = 0; index < existingCards.length - 1; index++) {
                existingCards[index].remove();
            }

            const source: DocumentFragment = template.content;
            const cardsFragment: DocumentFragment = document.createDocumentFragment();
            for (const card of cards) {
                cardsFragment.appendChild(render(source, card));
            }
            container.insertBefore(cardsFragment, container.lastElementChild);

            const counter = this.querySelector<HTMLElement>('.rose-counter');
            if (counter) {
                counter.dataset.slidesNum = `${this.itemsTotal + 1}`;
            }
        }

        this.reset(this.startingSlideIndex);
    }

    private async getCards(productCodes: string): Promise<RoseCardAPIResponseContract> {
        if (productCodes) {
            if (!this.apiClient) {
                this.apiClient = await RoseAPIClient.build({
                    endpoint: BundledSettingsProvider.newInstance().getSettingSync<string>('rose/endpoint')
                });
            }
            const apiResponse = await this.apiClient.productDetail(productCodes);
            return apiResponse?.data;
        }
        return null;
    }
}
